import { useRef, useState, useCallback, useEffect } from "react";

/**
 * BeforeAfter — interactive slider with draggable divider.
 * Both images must have the same aspect ratio for best results.
 */
export default function BeforeAfter({
  beforeSrc,
  afterSrc,
  beforeLabel = "Vorher",
  afterLabel = "Nachher",
  className = "",
}) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50); // %
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPos((x / rect.width) * 100);
  }, []);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // Keyboard accessibility
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onKey = (e) => {
      if (document.activeElement !== el) return;
      if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 4));
      if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 4));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      className={`relative w-full select-none overflow-hidden cursor-ew-resize touch-none outline-none ${className}`}
      aria-label="Vorher Nachher Schieberegler"
      role="slider"
      aria-valuenow={Math.round(pos)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {/* AFTER image (background, full) */}
      <img
        src={afterSrc}
        alt={afterLabel}
        className="block w-full h-full object-cover pointer-events-none"
        draggable={false}
      />

      {/* BEFORE image (clipped from left) */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${pos}%` }}
      >
        <img
          src={beforeSrc}
          alt={beforeLabel}
          className="block h-full w-auto max-w-none object-cover pointer-events-none"
          style={{
            width: containerRef.current ? `${containerRef.current.clientWidth}px` : "100%",
            height: "100%",
          }}
          draggable={false}
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/70 text-white text-[11px] uppercase tracking-[0.25em] backdrop-blur-sm">
        {beforeLabel}
      </div>
      <div className="absolute top-4 right-4 px-3 py-1.5 bg-white/85 text-[#1C1C1C] text-[11px] uppercase tracking-[0.25em] backdrop-blur-sm">
        {afterLabel}
      </div>

      {/* Divider line + handle */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-white pointer-events-none"
        style={{ left: `calc(${pos}% - 1px)` }}
      >
        <div
          data-testid="before-after-handle"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 w-12 h-12 rounded-full bg-white shadow-[0_4px_20px_rgba(0,0,0,0.25)] flex items-center justify-center"
        >
          <span className="text-[#0A0A0A] text-lg leading-none font-semibold">‹›</span>
        </div>
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E10600]" />
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E10600]" />
      </div>
    </div>
  );
}
